import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTypedSearchParams } from "react-router-typesafe-routes";
import {
  ActivateDataSourceDocument,
  type ActivateDataSourceMutation,
  type ActivateDataSourceMutationVariables,
  DeactivateDataSourceDocument,
  type DeactivateDataSourceMutation,
  type DeactivateDataSourceMutationVariables,
  GetDataSourceDetailDocument,
  GetDataSourcesDocument,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { useActionWithConfirm } from "~/lib/handleActionWithConfirm";
import { rootRoute as R } from "~/routes";

type DataSource = {
  id: string;
  name: string;
};

type UseDataSourceActivationParams = {
  importProjectId: string;
  canExecute?: () => boolean;
};

export const useDataSourceActivation = ({ importProjectId, canExecute }: UseDataSourceActivationParams) => {
  const { t } = useTranslation();
  const [, setSearchParams] = useTypedSearchParams(R.dataImportProjects.list);
  const handleActionWithConfirm = useActionWithConfirm();
  const [deactivateDataSource] = useMutation<DeactivateDataSourceMutation, DeactivateDataSourceMutationVariables>(
    DeactivateDataSourceDocument,
  );
  const [activateDataSource] = useMutation<ActivateDataSourceMutation, ActivateDataSourceMutationVariables>(
    ActivateDataSourceDocument,
  );

  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const onDeactivate = useCallback(
    (dataSource: DataSource) => {
      handleActionWithConfirm({
        confirmMessage: t("ui.deactivateX", { x: dataSource.name }),
        successMessage: t("feature.common.deactivatedSuccessfully", { model: "dataSource" }),
        action: () => {
          setSearchParams((prev) => ({ ...prev, selectedDataSource: undefined }));
          return deactivateDataSource({
            variables: { input: { tenantId, id: dataSource.id } },
            refetchQueries: [
              {
                query: GetDataSourcesDocument,
                variables: {
                  tenantId,
                  where: { dataSourceImportProjects: { dataImportProjectId: { _eq: importProjectId } } },
                },
              },
              { query: GetDataSourceDetailDocument, variables: { tenantId, dataSourceId: dataSource.id } },
            ],
          });
        },
        canExecute,
      });
    },
    [handleActionWithConfirm, t, deactivateDataSource, setSearchParams, tenantId, importProjectId, canExecute],
  );

  const onActivate = useCallback(
    (dataSource: DataSource) => {
      handleActionWithConfirm({
        confirmMessage: t("ui.activateX", { x: dataSource.name }),
        successMessage: t("feature.common.activatedSuccessfully", { model: "dataSource" }),
        action: () =>
          activateDataSource({
            variables: { input: { tenantId, id: dataSource.id } },
            refetchQueries: [
              {
                query: GetDataSourcesDocument,
                variables: {
                  tenantId,
                  where: { dataSourceImportProjects: { dataImportProjectId: { _eq: importProjectId } } },
                },
              },
              { query: GetDataSourceDetailDocument, variables: { tenantId, dataSourceId: dataSource.id } },
            ],
          }),
        canExecute,
      });
    },
    [handleActionWithConfirm, t, activateDataSource, tenantId, importProjectId, canExecute],
  );

  return {
    onActivate,
    onDeactivate,
  };
};
